import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiArrowLeft, FiStar, FiCalendar, FiClock, FiUsers, FiHash,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getBlindOfferRegistrations } from '../../services/api';
import styles from './BlindOffersView.module.css';

// -- Helpers -------------------------------------------------------------------
const toLocalDate = (d) => {
  if (!d) return null;
  const s = typeof d === 'string' ? d : d.toISOString();
  const [y, m, day] = s.substring(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day);
};
const fmt = (d) =>
  toLocalDate(d)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) ?? '—';

// -- Page ----------------------------------------------------------------------
const BlindOffersViewPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeDerasarId, isDerasarAdmin } = useAuth();

  const ev = location.state?.event || null;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeDerasarId) return;
    setLoading(true);
    getBlindOfferRegistrations(eventId, activeDerasarId)
      .then((res) => setCategories(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error('Failed to load blind offer registrations'))
      .finally(() => setLoading(false));
  }, [eventId, activeDerasarId]);

  const totalRegistrations = categories.reduce((s, c) => s + (c.registrations?.length || 0), 0);
  const totalQuantity      = categories.reduce(
    (s, c) => s + (c.registrations || []).reduce((q, r) => q + (r.quantity || 1), 0), 0
  );

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className={styles.page}>

      {/* Header */}
      <div className="page-header">
        <h2><FiStar /> Blind Offers</h2>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/derasar/events')}>
          <FiArrowLeft /> Back to Events
        </button>
      </div>

      {/* Event strip */}
      {ev && (
        <div className={styles.eventStrip}>
          <span className={styles.eventTitle}>{ev.title}</span>
          <span className={styles.eventMeta}>
            <FiCalendar size={13} />
            {fmt(ev.eventDate)}{ev.endDate ? ` – ${fmt(ev.endDate)}` : ''}
          </span>
          <span className={styles.eventMeta}>
            <FiClock size={13} /> Contributions until {fmt(ev.contributionEndDate)}
          </span>
        </div>
      )}

      {/* Summary pills */}
      {totalRegistrations > 0 && (
        <div className={styles.summaryRow}>
          <div className={styles.summaryPill}>
            <FiUsers size={14} />
            <span><strong>{totalRegistrations}</strong> registrant{totalRegistrations !== 1 ? 's' : ''}</span>
          </div>
          <div className={styles.summaryPill}>
            <FiHash size={14} />
            <span><strong>{totalQuantity}</strong> total offer slots</span>
          </div>
        </div>
      )}

      {/* Per-category sections */}
      {categories.length === 0 ? (
        <div className={styles.emptyState}>
          <FiStar size={40} />
          <p>No blind offer categories configured for this event.</p>
        </div>
      ) : (
        <div className={styles.sections}>
          {categories.map((cat) => {
            const regs = cat.registrations || [];
            return (
              <div key={cat.id} className={styles.categoryBlock}>

                {/* Category header */}
                <div className={styles.catHeader}>
                  <div className={styles.catLeft}>
                    <FiStar size={14} className={styles.catIcon} />
                    <span className={styles.catName}>{cat.category?.name}</span>
                    {cat.settledAt && (
                      <span className={styles.settledBadge}>✓ Settled</span>
                    )}
                  </div>
                  <div className={styles.catMeta}>
                    <span>{regs.length} registered</span>
                    {cat.maxCount && <span>/ {cat.maxCount} max</span>}
                    <span>
                      {regs.reduce((s, r) => s + (r.quantity || 1), 0)} total slots
                    </span>
                  </div>
                </div>

                {/* Registrations table */}
                {regs.length === 0 ? (
                  <div className={styles.emptyCategory}>No registrations yet.</div>
                ) : (
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th className={styles.th}>#</th>
                          <th className={styles.th}>Member</th>
                          <th className={`${styles.th} ${styles.center}`}>Qty</th>
                          <th className={styles.th}>Date</th>
                          {isDerasarAdmin && <th className={styles.th}>Status</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {regs.map((r, i) => (
                          <tr key={r.id} className={styles.tr}>
                            <td className={`${styles.td} ${styles.rowNum}`}>{i + 1}</td>
                            <td className={styles.td}>
                              <div className={styles.personCell}>
                                <span className={styles.personName}>{r.member?.name || '—'}</span>
                                {r.member?.phone && (
                                  <span className={styles.personPhone}>{r.member.phone}</span>
                                )}
                              </div>
                            </td>
                            <td className={`${styles.td} ${styles.center}`}>
                              <span className={`${styles.qtyBadge} ${r.quantity > 1 ? styles.qtyBadgeHigh : ''}`}>
                                {r.quantity || 1}
                              </span>
                            </td>
                            <td className={styles.td}>
                              {r.registrationDate ? fmt(r.registrationDate) : (
                                <span className={styles.muted}>—</span>
                              )}
                            </td>
                            {isDerasarAdmin && (
                              <td className={styles.td}>
                                {r.offeringId ? (
                                  <span className={styles.statusSettled}>Settled</span>
                                ) : (
                                  <span className={styles.statusPending}>Pending</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

export default BlindOffersViewPage;
